import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import { OrderStatus, type OrderStatus as OS } from '@/types/enums'
import type { Order, OrderComment, StatusHistoryRow } from '@/types/order'
import { getForwardTargets, isActiveStatus } from '@/utils/status-transitions'
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'

export interface OrderListParams {
  page?: number
  pageSize?: number
  status?: OS[]
  inspectorId?: string
  managerId?: string
  /**
   * Любая роль пользователя (manager OR inspector). Серверный фильтр
   * нужен, чтобы `UserDetailPage` не качал сотни чужих заказов и не
   * фильтровал их на клиенте.
   */
  userId?: string
  companyId?: string
  dateFrom?: string
  dateTo?: string
  /**
   * Поле даты, по которому применяются `dateFrom`/`dateTo`. По умолчанию
   * — `created_at` (как и было исторически). Для биллинга нужна «дата
   * завершения» — задаём `status_updated_at`: у терминальных заказов это
   * момент перехода в терминальный статус (см. `TARIFFS_SPEC.md` §9.1).
   */
  dateField?: 'created_at' | 'status_updated_at'
  city?: string
  search?: string
  /** Фильтр по типу заказчика: 'company' (B2B) | 'individual' (Физлицо). */
  customerKind?: 'company' | 'individual'
}

export interface OrderListResult {
  items: Order[]
  total: number
}

async function mockListOrders(params: OrderListParams): Promise<OrderListResult> {
  const page = Math.max(1, params.page ?? 1)
  const pageSize = Math.min(100, params.pageSize ?? 25)
  let rows = [...mockState.orders]

  if (params.status?.length) {
    const set = new Set(params.status)
    rows = rows.filter((o) => set.has(o.status as OS))
  }
  if (params.inspectorId) {
    rows = rows.filter((o) => o.inspector_id === params.inspectorId)
  }
  if (params.managerId) {
    rows = rows.filter((o) => o.manager_id === params.managerId)
  }
  if (params.userId) {
    rows = rows.filter(
      (o) =>
        o.manager_id === params.userId || o.inspector_id === params.userId,
    )
  }
  if (params.companyId) {
    rows = rows.filter(
      (o) =>
        o.customer_company_id === params.companyId ||
        o.contractor_company_id === params.companyId,
    )
  }
  const dateField = params.dateField ?? 'created_at'
  if (params.dateFrom) {
    const t = new Date(params.dateFrom).getTime()
    rows = rows.filter((o) => new Date(o[dateField]).getTime() >= t)
  }
  if (params.dateTo) {
    const t = new Date(params.dateTo).getTime()
    rows = rows.filter((o) => new Date(o[dateField]).getTime() <= t)
  }
  if (params.city) {
    const c = params.city.toLowerCase()
    rows = rows.filter((o) => (o.address_city ?? '').toLowerCase() === c)
  }
  if (params.search?.trim()) {
    const q = params.search.trim().toLowerCase()
    rows = rows.filter((o) => {
      const blob = [
        o.vin,
        o.address,
        o.brand,
        o.model,
        o.seller_full_name,
        o.seller_phone,
        o.id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return blob.includes(q)
    })
  }

  rows.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
  const total = rows.length
  const start = (page - 1) * pageSize
  const items = rows.slice(start, start + pageSize)
  return { items, total }
}

async function mockGetOrder(id: string): Promise<Order | null> {
  return mockState.orders.find((o) => o.id === id) ?? null
}

async function mockStatusHistory(orderId: string): Promise<StatusHistoryRow[]> {
  return mockState.statusHistory
    .filter((h) => h.order_id === orderId)
    .sort(
      (a, b) =>
        new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
    )
}

/** Adapts DB photos text[] to OrderPhoto[] */
function adaptPhotos(raw: unknown): Order['photos'] {
  if (!Array.isArray(raw)) return []
  return (raw as string[]).map((url) => ({ url }))
}

export async function listOrders(
  params: OrderListParams,
): Promise<OrderListResult> {
  if (USE_MOCK) return mockListOrders(params)

  const supabase = createClient()
  const page = Math.max(1, params.page ?? 1)
  const pageSize = Math.min(100, params.pageSize ?? 25)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  // FK orders.customer/contractor_company_id → companies, подтягиваем embed.
  // Профили (dealer/inspector) — FK на auth.users, embed через PostgREST
  // не работает (PGRST200). Имена резолвим вторым запросом ниже.
  let query = supabase
    .from('orders')
    .select(
      `*,
       customer_company:companies!customer_company_id(id, name, phone),
       contractor_company:companies!contractor_company_id(id, name, phone)`,
      { count: 'exact' },
    )

  if (params.status?.length) {
    query = query.in('status', params.status)
  }
  if (params.inspectorId) {
    query = query.eq('inspector_id', params.inspectorId)
  }
  if (params.managerId) {
    query = query.eq('manager_id', params.managerId)
  }
  if (params.userId) {
    query = query.or(
      `manager_id.eq.${params.userId},inspector_id.eq.${params.userId}`,
    )
  }
  if (params.companyId) {
    query = query.or(`customer_company_id.eq.${params.companyId},contractor_company_id.eq.${params.companyId}`)
  }
  const dateField = params.dateField ?? 'created_at'
  if (params.dateFrom) {
    query = query.gte(dateField, params.dateFrom)
  }
  if (params.dateTo) {
    query = query.lte(dateField, params.dateTo)
  }
  if (params.city) {
    query = query.eq('address_city', params.city)
  }
  if (params.customerKind) {
    query = query.eq('customer_kind', params.customerKind)
  }
  if (params.search?.trim()) {
    // Экранируем символы, ломающие PostgREST .or() парсер: запятая, скобки.
    const q = params.search.trim().replace(/[(),]/g, ' ')
    const parts = [
      `vin.ilike.%${q}%`,
      `address.ilike.%${q}%`,
      `brand.ilike.%${q}%`,
      `model.ilike.%${q}%`,
      `seller_full_name.ilike.%${q}%`,
      `seller_phone.ilike.%${q}%`,
      `individual_customer_name.ilike.%${q}%`,
      `individual_customer_phone.ilike.%${q}%`,
    ]
    // Поиск по номеру заказа — в UI показываются первые 8 hex-символов UUID.
    // PostgREST не умеет `id::text.ilike.…` внутри `or()` (PGRST100 — каст `::`
    // ломает парсер логического дерева, и весь поиск падает). Поэтому для
    // hex-префиксов 2–8 символов строим диапазон UUID-границ и оборачиваем в
    // `and(id.gte.{min},id.lte.{max})` — UUID-сравнение в Postgres побайтовое
    // и совпадает с лексикографическим порядком hex-представления.
    if (/^[0-9a-fA-F]{2,8}$/.test(q)) {
      const lower = q.toLowerCase()
      const padLow = lower.padEnd(8, '0')
      const padHigh = lower.padEnd(8, 'f')
      const minUuid = `${padLow}-0000-0000-0000-000000000000`
      const maxUuid = `${padHigh}-ffff-ffff-ffff-ffffffffffff`
      parts.push(`and(id.gte.${minUuid},id.lte.${maxUuid})`)
    }
    query = query.or(parts.join(','))
  }

  const { data, error, count } = await query
    .order(dateField, { ascending: false })
    .range(from, to)

  if (error) throw new Error(error.message)

  const rawRows = (data ?? []) as Array<Record<string, unknown>>

  // Bulk-резолв ФИО/телефонов dealer/inspector. Без этого колонки
  // «Инспектор»/«Менеджер» в таблице рисовали UUID (profileName шёл в mockState).
  const profileIds = Array.from(
    new Set(
      rawRows
        .flatMap((r) => [r.manager_id as string | null, r.inspector_id as string | null])
        .filter((v): v is string => !!v),
    ),
  )
  let profById = new Map<string, { id: string; full_name: string; phone: string | null; company_id: string | null }>()
  if (profileIds.length > 0) {
    const { data: profs, error: profErr } = await supabase
      .from('profiles')
      .select('id, full_name, phone, company_id')
      .in('id', profileIds)
    if (profErr) throw new Error(profErr.message)
    profById = new Map(
      (profs ?? []).map((p) => [
        p.id as string,
        {
          id: p.id as string,
          full_name: p.full_name as string,
          phone: (p.phone as string | null) ?? null,
          company_id: (p.company_id as string | null) ?? null,
        },
      ]),
    )
  }

  const items = rawRows.map((row) => ({
    ...row,
    photos: adaptPhotos(row.photos),
    manager: row.manager_id ? profById.get(row.manager_id as string) ?? null : null,
    inspector_profile: row.inspector_id
      ? profById.get(row.inspector_id as string) ?? null
      : null,
  })) as Order[]

  return { items, total: count ?? 0 }
}

export async function getOrder(id: string): Promise<Order | null> {
  if (USE_MOCK) return mockGetOrder(id)

  const supabase = createClient()

  // Companies can be joined via FK; profiles must be fetched separately (manager_id → auth.users)
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      customer_company:companies!customer_company_id(id, name, phone),
      contractor_company:companies!contractor_company_id(id, name, phone)
    `)
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(error.message)
  }

  // Fetch manager and inspector profiles (FK is on auth.users, not public.profiles)
  const profileIds = [data.manager_id, data.inspector_id].filter(Boolean) as string[]
  let manager = null
  let inspector_profile = null
  if (profileIds.length) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, phone, company_id')
      .in('id', profileIds)
    if (profiles) {
      manager = profiles.find((p) => p.id === data.manager_id) ?? null
      inspector_profile = profiles.find((p) => p.id === data.inspector_id) ?? null
    }
  }

  // Fallback: если contractor_company_id не записан в заказ, но у инспектора
  // есть company_id — подтягиваем её, чтобы UI показывал «Исполнитель».
  let contractor_company = data.contractor_company
  if (!contractor_company && inspector_profile?.company_id) {
    const { data: c } = await supabase
      .from('companies')
      .select('id, name, phone')
      .eq('id', inspector_profile.company_id)
      .maybeSingle()
    if (c) contractor_company = c
  }

  // Паспорт продавца и реквизиты выплаты вынесены из orders.* в отдельные
  // таблицы buyer_passports/buyer_payouts (1:1 по order_id) миграцией stryzh
  // `20260520120000_extract_buyer_passport_payout.sql`. Чтобы не править
  // OrderDetailPage и тип Order, подгружаем строки и мерджим ключи в
  // объект заказа под legacy-именами `passport_*` / `payout_sbp_phone`.
  // Те же поля плюс `passport_selfie_url` (новое — anti-fraud селфи).
  type BuyerPassportRow = {
    series_number: string | null
    last_name: string | null
    first_name: string | null
    middle_name: string | null
    birth_date: string | null
    issued_by: string | null
    issue_date: string | null
    division_code: string | null
    registration_locality: string | null
    registration_address: string | null
    main_scan_url: string | null
    registration_scan_url: string | null
    selfie_url: string | null
    manual_entry: boolean
    data_saved_at: string | null
  }
  type BuyerPayoutRow = { sbp_phone: string | null }
  const [bpRes, payoutRes] = await Promise.all([
    supabase
      .from('buyer_passports')
      .select(
        'series_number, last_name, first_name, middle_name, birth_date, ' +
          'issued_by, issue_date, division_code, registration_locality, ' +
          'registration_address, main_scan_url, registration_scan_url, ' +
          'selfie_url, manual_entry, data_saved_at',
      )
      .eq('order_id', id)
      .maybeSingle(),
    supabase
      .from('buyer_payouts')
      .select('sbp_phone')
      .eq('order_id', id)
      .maybeSingle(),
  ])
  const bp = bpRes.data as BuyerPassportRow | null
  const payout = payoutRes.data as BuyerPayoutRow | null

  const passportMerged: Record<string, unknown> = bp
    ? {
        passport_series_number: bp.series_number,
        passport_last_name: bp.last_name,
        passport_first_name: bp.first_name,
        passport_middle_name: bp.middle_name,
        passport_birth_date: bp.birth_date,
        passport_issued_by: bp.issued_by,
        passport_issue_date: bp.issue_date,
        passport_division_code: bp.division_code,
        passport_registration_locality: bp.registration_locality,
        passport_registration_address: bp.registration_address,
        passport_main_scan_url: bp.main_scan_url,
        passport_registration_scan_url: bp.registration_scan_url,
        passport_selfie_url: bp.selfie_url,
        passport_manual_entry: bp.manual_entry,
        passport_data_saved_at: bp.data_saved_at,
      }
    : {}

  return {
    ...data,
    ...passportMerged,
    payout_sbp_phone: payout?.sbp_phone ?? null,
    contractor_company,
    photos: adaptPhotos(data.photos),
    manager,
    inspector_profile,
  } as Order
}

export async function getStatusHistory(
  orderId: string,
): Promise<StatusHistoryRow[]> {
  if (USE_MOCK) return mockStatusHistory(orderId)

  const supabase = createClient()
  const { data, error } = await supabase
    .from('status_history')
    .select('*')
    .eq('order_id', orderId)
    .order('changed_at', { ascending: false })

  if (error) throw new Error(error.message)
  const rows = (data ?? []) as StatusHistoryRow[]

  // Resolve changed_by profiles
  const actorIds = [...new Set(rows.map((r) => r.changed_by).filter(Boolean))]
  if (actorIds.length) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, phone, company_id')
      .in('id', actorIds)
    if (profiles) {
      const byId = Object.fromEntries(profiles.map((p) => [p.id, p]))
      return rows.map((r) => ({
        ...r,
        changed_by_profile: byId[r.changed_by] ?? null,
      }))
    }
  }

  return rows
}

export async function changeOrderStatus(
  orderId: string,
  newStatus: OS,
  _reason?: string,
): Promise<void> {
  if (!USE_MOCK) {
    const supabase = createClient()
    const { error } = await supabase.rpc('change_order_status', {
      p_order_id: orderId,
      p_new_status: newStatus,
      p_reason: _reason ?? null,
    })
    if (error) throw new Error(error.message)
    return
  }
  const o = mockState.orders.find((x) => x.id === orderId)
  if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
  const allowed = getForwardTargets(o.status as OS, o.customer_kind)
  if (!allowed.includes(newStatus)) {
    throw new Error(i18n.t('apiErr.orders.invalidStatusTransition'))
  }
  const from = o.status as OS
  o.status = newStatus
  o.status_updated_at = new Date().toISOString()
  const row: StatusHistoryRow = {
    id: `sh-${crypto.randomUUID()}`,
    order_id: orderId,
    from_status: from,
    to_status: newStatus,
    changed_at: o.status_updated_at,
    changed_by: 'user-admin-1',
    history_label: undefined,
  }
  mockState.statusHistory.unshift(row)
  mockState.auditLog.unshift({
    id: `audit-${crypto.randomUUID()}`,
    created_at: o.status_updated_at,
    user_id: 'user-admin-1',
    action: 'change_status',
    object_type: 'order',
    object_id: orderId,
    details: { before: { status: from }, after: { status: newStatus } },
  })
  mockState.events.unshift({
    id: `ev-${crypto.randomUUID()}`,
    order_id: orderId,
    created_at: o.status_updated_at,
    summary: `Статус: ${from} → ${newStatus}`,
    status: newStatus,
  })
  trimEvents()
}

/**
 * Возврат заказа на «Поиск» (SEARCH). Админ-действие «искать другого инспектора».
 * Идёт через ту же RPC change_order_status (admin/dispatcher → bypass проверок переходов),
 * которая при p_new_status='SEARCH' атомарно обнуляет inspector_id на стороне БД.
 */
export async function returnOrderToSearch(
  orderId: string,
  reason?: string,
): Promise<void> {
  if (!USE_MOCK) {
    const supabase = createClient()
    const { error } = await supabase.rpc('change_order_status', {
      p_order_id: orderId,
      p_new_status: OrderStatus.SEARCH,
      p_reason: reason ?? 'admin_return_to_search',
    })
    if (error) throw new Error(error.message)
    return
  }
  const o = mockState.orders.find((x) => x.id === orderId)
  if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
  const from = o.status as OS
  const ts = new Date().toISOString()
  o.status = OrderStatus.SEARCH
  o.inspector_id = null
  o.status_updated_at = ts
  mockState.statusHistory.unshift({
    id: `sh-${crypto.randomUUID()}`,
    order_id: orderId,
    from_status: from,
    to_status: OrderStatus.SEARCH,
    changed_at: ts,
    changed_by: 'user-admin-1',
    history_label: 'Возврат в поиск',
  })
  mockState.auditLog.unshift({
    id: `audit-${crypto.randomUUID()}`,
    created_at: ts,
    user_id: 'user-admin-1',
    action: 'change_status',
    object_type: 'order',
    object_id: orderId,
    details: { before: { status: from }, after: { status: OrderStatus.SEARCH } },
  })
  mockState.events.unshift({
    id: `ev-${crypto.randomUUID()}`,
    order_id: orderId,
    created_at: ts,
    summary: `Статус: ${from} → ${OrderStatus.SEARCH}`,
    status: OrderStatus.SEARCH,
  })
  trimEvents()
}

function trimEvents() {
  mockState.events = mockState.events.slice(0, 50)
}

export async function cancelOrder(orderId: string, reason: string): Promise<void> {
  if (!USE_MOCK) {
    const supabase = createClient()
    const { error } = await supabase.rpc('change_order_status', {
      p_order_id: orderId,
      p_new_status: OrderStatus.CANCELLED,
      p_reason: reason,
    })
    if (error) throw new Error(error.message)
    return
  }
  const o = mockState.orders.find((x) => x.id === orderId)
  if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
  if (!isActiveStatus(o.status as OS)) {
    throw new Error(i18n.t('apiErr.orders.alreadyCancelled'))
  }
  const from = o.status as OS
  const ts = new Date().toISOString()
  const snapshot = { ...o }
  o.status = OrderStatus.CANCELLED
  o.status_updated_at = ts
  mockState.statusHistory.unshift({
    id: `sh-${crypto.randomUUID()}`,
    order_id: orderId,
    from_status: from,
    to_status: OrderStatus.CANCELLED,
    changed_at: ts,
    changed_by: 'user-admin-1',
    history_label: 'Отмена',
  })
  mockState.auditLog.unshift({
    id: `audit-${crypto.randomUUID()}`,
    created_at: ts,
    user_id: 'user-admin-1',
    action: 'cancel_order',
    object_type: 'order',
    object_id: orderId,
    details: {
      reason,
      snapshot_fields: Object.keys(snapshot),
    },
  })
  mockState.events.unshift({
    id: `ev-${crypto.randomUUID()}`,
    order_id: orderId,
    created_at: ts,
    summary: `Заказ отменён: ${reason}`,
    status: OrderStatus.CANCELLED,
  })
  trimEvents()
}

export interface CreateOrderPayload {
  brand: string
  model: string
  vin?: string | null
  address: string
  address_city?: string | null
  /**
   * Оценочная цена в рублях. В БД это колонка `orders.desired_price` (см.
   * миграцию `20260419140000_tariffs_triggers.sql`); триггер
   * `orders_build_tariff_snapshot_before_insert` переводит её в миллионы
   * для подбора К2.
   */
  desired_price?: number | null
  /** Год выпуска ТС (опционально). См. миграцию 20260520150000. */
  vehicle_year?: number | null
  /** Гос. номер ТС (опционально, в нормализованном виде без пробелов). */
  license_plate?: string | null
  seller_full_name?: string | null
  seller_phone?: string | null
  order_comment?: string | null
  manager_id: string
  customer_company_id?: string | null
  contractor_company_id?: string | null
  inspector_id?: string | null
  /** По умолчанию 'SEARCH'. Передать 'DRAFT' для сохранения черновика. */
  status?: 'DRAFT' | 'SEARCH'
}

export async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const orderStatus = (payload.status ?? 'SEARCH') as OS
  if (USE_MOCK) {
    const now = new Date().toISOString()
    const order: Order = {
      id: `order-${crypto.randomUUID().slice(0, 8)}`,
      status: orderStatus,
      brand: payload.brand,
      model: payload.model,
      vin: payload.vin ?? null,
      vehicle_year: payload.vehicle_year ?? null,
      license_plate: payload.license_plate ?? null,
      address: payload.address,
      address_city: payload.address_city ?? undefined,
      desired_price: payload.desired_price ?? null,
      final_price: null,
      manager_id: payload.manager_id,
      inspector_id: payload.inspector_id ?? null,
      customer_company_id: payload.customer_company_id ?? '',
      contractor_company_id: payload.contractor_company_id ?? '',
      photos: [],
      created_at: now,
      status_updated_at: now,
      dkp_formed_at: null,
      payment_sent_at: null,
      transportation_started_at: null,
      vehicle_handed_to_customer_at: null,
      transportation_confirmed_at: null,
      external_inspection_report_url: null,
      external_inspection_report_verified_at: null,
      seller_full_name: payload.seller_full_name ?? undefined,
      seller_phone: payload.seller_phone ?? null,
      order_comment: payload.order_comment ?? null,
    }
    mockState.orders.unshift(order)
    return order
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('orders')
    .insert({
      brand: payload.brand,
      model: payload.model,
      vin: payload.vin || null,
      vehicle_year: payload.vehicle_year ?? null,
      license_plate: payload.license_plate ?? null,
      address: payload.address,
      address_city: payload.address_city || null,
      desired_price: payload.desired_price ?? null,
      seller_full_name: payload.seller_full_name || null,
      seller_phone: payload.seller_phone || null,
      order_comment: payload.order_comment || null,
      manager_id: payload.manager_id,
      customer_company_id: payload.customer_company_id ?? null,
      contractor_company_id: payload.contractor_company_id ?? null,
      inspector_id: payload.inspector_id ?? null,
      status: orderStatus,
    })
    .select()
    .single()

  if (error) throw new Error(error.message)
  return { ...data, photos: [] } as Order
}

export interface AdminUpdateOrderPayload {
  brand?: string | null
  model?: string | null
  address?: string | null
  address_city?: string | null
  seller_name?: string | null
  seller_phone?: string | null
  vin?: string | null
  /** Год выпуска ТС (опционально). */
  vehicle_year?: number | null
  /** Гос. номер в нормализованном виде без пробелов (см. `normalizeLicensePlate`). */
  license_plate?: string | null
}

export async function adminUpdateOrder(
  orderId: string,
  payload: AdminUpdateOrderPayload,
): Promise<{ ok: boolean; changes_count: number }> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    if (payload.brand != null) o.brand = payload.brand
    if (payload.model != null) o.model = payload.model
    if (payload.address != null) o.address = payload.address
    if (payload.address_city != null) o.address_city = payload.address_city
    if (payload.seller_name != null) o.seller_full_name = payload.seller_name
    if (payload.seller_phone != null) o.seller_phone = payload.seller_phone
    if (payload.vin != null) o.vin = payload.vin
    if (payload.vehicle_year !== undefined) o.vehicle_year = payload.vehicle_year
    if (payload.license_plate !== undefined) o.license_plate = payload.license_plate
    return { ok: true, changes_count: 1 }
  }

  const supabase = createClient()
  const { data, error } = await supabase.rpc('admin_update_order', {
    p_order_id: orderId,
    p_brand: payload.brand ?? null,
    p_model: payload.model ?? null,
    p_address: payload.address ?? null,
    p_address_city: payload.address_city ?? null,
    p_seller_name: payload.seller_name ?? null,
    p_seller_phone: payload.seller_phone ?? null,
    p_vin: payload.vin ?? null,
    p_vehicle_year: payload.vehicle_year ?? null,
    p_license_plate: payload.license_plate ?? null,
  })
  if (error) throw new Error(error.message)
  return data as { ok: boolean; changes_count: number }
}

// Mock-хранилище комментариев заказа (только для USE_MOCK / preview).
const mockOrderComments: OrderComment[] = []

/**
 * Лента внутренних комментариев диспетчера к заказу (append-only, новые
 * сверху). Видна только admin/dispatcher (RLS). Подтягиваем имена авторов
 * одним запросом — как в истории лидов.
 */
export async function listOrderComments(orderId: string): Promise<OrderComment[]> {
  if (USE_MOCK) {
    return mockOrderComments
      .filter((c) => c.order_id === orderId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('order_comments')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(500)
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as Array<Omit<OrderComment, 'author_name'>>

  const userIds = Array.from(
    new Set(rows.map((r) => r.author_id).filter((v): v is string => !!v)),
  )
  const nameById = new Map<string, string>()
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds)
    for (const p of (profiles ?? []) as Array<{ id: string; full_name: string | null }>) {
      nameById.set(p.id, p.full_name ?? '')
    }
  }
  return rows.map((r) => ({
    ...r,
    author_name: r.author_id ? (nameById.get(r.author_id) || null) : null,
  }))
}

/**
 * Добавить комментарий к заказу. author_id проставляет БД из auth.uid()
 * (триггер), поэтому из клиента передаём только текст. Пустой — не пишем.
 */
export async function addOrderComment(orderId: string, body: string): Promise<void> {
  const trimmed = body.trim()
  if (trimmed === '') return
  if (USE_MOCK) {
    mockOrderComments.push({
      id: `mock-${mockOrderComments.length + 1}`,
      order_id: orderId,
      author_id: 'mock-admin',
      body: trimmed,
      created_at: new Date().toISOString(),
      author_name: 'Демо диспетчер',
    })
    return
  }
  const supabase = createClient()
  const { error } = await supabase
    .from('order_comments')
    .insert({ order_id: orderId, body: trimmed })
  if (error) throw new Error(error.message)
}

/**
 * Подтвердить адрес доставки заказа. Вызывает RPC `confirm_delivery_address`
 * (миграция stryzh 20260521130000). Доступно для manager-владельца,
 * customer_supervisor его компании, dispatcher, admin. Статус заказа должен
 * быть PAYMENT_TRANSFER или TRANSPORTATION, и инспектор ещё не выехал
 * (`transportation_started_at IS NULL`). Timestamp `confirmed_at`
 * проставляется только при первом вызове; повторные правки до выезда
 * timestamp не обновляют.
 */
export async function confirmDeliveryAddress(args: {
  orderId: string
  address: string
  lat: number
  lon: number
}): Promise<void> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === args.orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    if (o.transportation_started_at) {
      throw new Error(i18n.t('apiErr.orders.deliveryAddressFrozen'))
    }
    o.delivery_address = args.address.trim()
    o.delivery_address_lat = args.lat
    o.delivery_address_lon = args.lon
    o.delivery_address_confirmed_at =
      o.delivery_address_confirmed_at ?? new Date().toISOString()
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('confirm_delivery_address', {
    p_order_id: args.orderId,
    p_address: args.address,
    p_lat: args.lat,
    p_lon: args.lon,
  })
  if (error) throw new Error(translateDeliveryAddressError(error.message))
}

/**
 * Сбросить подтверждение адреса доставки (только admin/dispatcher и пока
 * инспектор не выехал). Вызывает RPC `reset_delivery_address_confirmation`.
 */
export async function resetDeliveryAddressConfirmation(
  orderId: string,
): Promise<void> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    if (o.transportation_started_at) {
      throw new Error(i18n.t('apiErr.orders.deliveryAddressFrozen'))
    }
    o.delivery_address_confirmed_at = null
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('reset_delivery_address_confirmation', {
    p_order_id: orderId,
  })
  if (error) throw new Error(translateDeliveryAddressError(error.message))
}

function translateDeliveryAddressError(message: string): string {
  if (message.includes('delivery_address_frozen')) {
    return i18n.t('apiErr.orders.deliveryAddressFrozen')
  }
  if (message.includes('delivery_address_not_confirmed')) {
    return i18n.t('apiErr.orders.deliveryAddressNotConfirmed')
  }
  if (message.includes('forbidden')) {
    return i18n.t('apiErr.orders.deliveryAddressForbidden')
  }
  if (message.includes('invalid_status')) {
    return i18n.t('apiErr.orders.deliveryAddressInvalidStatus')
  }
  if (message.includes('address_required')) {
    return i18n.t('apiErr.orders.deliveryAddressRequired')
  }
  if (
    message.includes('coordinates_required') ||
    message.includes('invalid_coordinates')
  ) {
    return i18n.t('apiErr.orders.deliveryCoordinatesMissing')
  }
  if (message.includes('order_not_found')) {
    return i18n.t('apiErr.orders.notFoundDot')
  }
  return message
}

export async function logPdAccess(orderId: string): Promise<void> {
  if (!USE_MOCK) {
    const supabase = createClient()
    const { error } = await supabase.rpc('log_pd_access', { p_order_id: orderId })
    if (error) throw new Error(error.message)
    return
  }
  mockState.pdAccessLog.unshift({
    id: `pd-${crypto.randomUUID()}`,
    created_at: new Date().toISOString(),
    user_id: 'user-admin-1',
    order_id: orderId,
  })
}

/**
 * Ручное назначение инспектора админом/диспетчером. RPC ставит
 * status='ASSIGNED' и inspector_id; допустимо только из SEARCH/ASSIGNED.
 */
export async function adminAssignInspector(
  orderId: string,
  inspectorId: string,
): Promise<void> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    o.inspector_id = inspectorId
    o.status = 'ASSIGNED' as OS
    o.status_updated_at = new Date().toISOString()
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('admin_assign_inspector', {
    p_order_id: orderId,
    p_inspector_id: inspectorId,
  })
  if (error) throw new Error(mapAssignInspectorError(error.message))
}

/**
 * Сохранить URL внешнего отчёта осмотра. Используется админом при переводе
 * INSPECTION→APPRAISAL, когда инспектор не успел ввести URL в моб. приложении.
 * verified_at проставляется в now() — админ выступает ручным подтверждением.
 */
export async function setExternalInspectionReportUrl(
  orderId: string,
  url: string,
): Promise<void> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    o.external_inspection_report_url = url
    o.external_inspection_report_verified_at = new Date().toISOString()
    return
  }
  const supabase = createClient()
  const { error } = await supabase
    .from('orders')
    .update({
      external_inspection_report_url: url,
      external_inspection_report_verified_at: new Date().toISOString(),
    })
    .eq('id', orderId)
  if (error) throw new Error(error.message)
}

/**
 * Дата и время осмотра (orders.inspection_scheduled_at); null — снять.
 * Первое заполнение у заказа СберАвто шлёт Сберу событие «Назначен осмотр»
 * (триггер БД).
 */
export async function setInspectionScheduledAt(orderId: string, iso: string | null): Promise<void> {
  if (USE_MOCK) {
    const i = mockState.orders.findIndex((x) => x.id === orderId)
    if (i < 0) throw new Error(i18n.t('apiErr.orders.notFound'))
    mockState.orders[i] = { ...mockState.orders[i], inspection_scheduled_at: iso }
    return
  }
  const supabase = createClient()
  const { error } = await supabase
    .from('orders')
    .update({ inspection_scheduled_at: iso })
    .eq('id', orderId)
  if (error) throw new Error(error.message)
}

/**
 * Сохранить выкупную цену (final_price). Используется админом при переводе
 * APPRAISAL→BUYING. Допустимо только в статусе APPRAISAL — иначе ошибка.
 */
export async function setFinalPrice(
  orderId: string,
  priceRub: number,
): Promise<void> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    if (o.status !== 'APPRAISAL') {
      throw new Error(i18n.t('apiErr.orders.priceOnlyAtAppraisal'))
    }
    o.final_price = priceRub
    return
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('orders')
    .update({ final_price: priceRub })
    .eq('id', orderId)
    .eq('status', 'APPRAISAL')
    .select('id')
  if (error) throw new Error(error.message)
  if (!data || data.length === 0) {
    throw new Error(i18n.t('apiErr.orders.priceOnlyAtAppraisal'))
  }
}

function mapAssignInspectorError(raw: string): string {
  const s = raw || ''
  if (s.includes('inspector_already_busy') ||
      s.includes('idx_one_active_order_per_inspector') ||
      s.includes('duplicate key value')) {
    return i18n.t('apiErr.orders.inspectorAlreadyBusy')
  }
  if (s.includes('not_an_active_inspector')) {
    return i18n.t('apiErr.orders.notAnActiveInspector')
  }
  if (s.includes('inspector_not_found')) {
    return i18n.t('apiErr.orders.inspectorNotFound')
  }
  if (s.includes('order_not_assignable')) {
    return i18n.t('apiErr.orders.orderNotAssignable')
  }
  if (s.includes('order_not_found')) {
    return i18n.t('apiErr.orders.notFoundDot')
  }
  if (s.includes('forbidden')) {
    return i18n.t('apiErr.orders.assignForbidden')
  }
  return s
}

export async function publishDraftOrder(orderId: string): Promise<void> {
  if (USE_MOCK) {
    const o = mockState.orders.find((x) => x.id === orderId)
    if (!o) throw new Error(i18n.t('apiErr.orders.notFound'))
    o.status = 'SEARCH' as OS
    o.status_updated_at = new Date().toISOString()
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('publish_draft_order', { p_order_id: orderId })
  if (error) throw new Error(error.message)
}
